import { create } from "zustand"
import {
  applyNodeChanges,
  applyEdgeChanges,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
  type Connection,
  type Edge,
} from "@xyflow/react"
import type { PipelineNode, PipelineEdge, NodeData } from "@/lib/types/pipeline"
import { getModuleDefinition } from "@/lib/registry/modules"
import { getTextBuilderInputs, getStructuredOutputHandles, type OutputSchemaField } from "@/lib/registry/text-builder"
import { getCompositorInputs } from "@/lib/registry/compositor"
import { DEFAULT_COMPOSITOR_CONFIG } from "@/lib/types/compositor"
import { useModelCatalogStore } from "@/lib/stores/model-catalog-store"
import { PARAM_TYPE_TO_DATA_TYPE } from "@/lib/types/model-catalog"

function generateId(): string {
  return crypto.randomUUID().slice(0, 8)
}

interface PipelineStore {
  // State
  nodes: PipelineNode[]
  edges: PipelineEdge[]
  selectedNodeId: string | null
  isDirty: boolean
  collapsedNodeIds: Set<string>
  focusedNodeId: string | null
  focusNode: (nodeId: string | null) => void

  // React Flow callbacks
  onNodesChange: OnNodesChange<PipelineNode>
  onEdgesChange: OnEdgesChange
  onConnect: OnConnect

  // Actions
  addNode: (module: string, position: { x: number; y: number }) => string
  removeNode: (nodeId: string) => void
  removeEdge: (edgeId: string) => void
  updateNodeConfig: (nodeId: string, config: Record<string, unknown>) => void
  updateNodeLabel: (nodeId: string, label: string) => void
  selectNode: (nodeId: string | null) => void
  toggleNodeCollapsed: (nodeId: string) => void

  duplicateNode: (nodeId: string, offset?: { x: number; y: number }) => string

  // Raw actions — applied from agent tool results (backend already saved, no isDirty)
  addNodeRaw: (node: PipelineNode) => void
  removeNodeRaw: (nodeId: string) => void
  updateNodeRaw: (node: PipelineNode) => void
  addEdgeRaw: (edge: PipelineEdge) => void
  removeEdgeRaw: (edgeId: string) => void
  markClean: () => void

  // Bulk operations
  clearCanvas: () => void
  loadPipeline: (nodes: PipelineNode[], edges: PipelineEdge[]) => void
  needsFitView: boolean
  clearFitView: () => void

  // Connection validation
  isValidConnection: (connection: Edge | Connection) => boolean
  getRejectionReason: (connection: Edge | Connection) => string | null
}

export const usePipelineStore = create<PipelineStore>((set, get) => ({
  nodes: [],
  edges: [],
  selectedNodeId: null,
  isDirty: false,
  collapsedNodeIds: new Set(),
  focusedNodeId: null,
  focusNode: (nodeId) => set({ focusedNodeId: nodeId }),

  onNodesChange: (changes) => {
    set((state) => ({
      nodes: applyNodeChanges(changes, state.nodes),
      isDirty: true,
    }))
  },

  onEdgesChange: (changes) => {
    set((state) => ({
      edges: applyEdgeChanges(changes, state.edges),
      isDirty: true,
    }))
  },

  onConnect: (connection) => {
    const { isValidConnection } = get()
    if (!isValidConnection(connection)) return

    const edgeId = `e_${connection.source}_${connection.sourceHandle}_${connection.target}_${connection.targetHandle}`
    const newEdge: PipelineEdge = {
      id: edgeId,
      source: connection.source!,
      target: connection.target!,
      sourceHandle: connection.sourceHandle,
      targetHandle: connection.targetHandle,
      type: "typed",
    }

    set((state) => ({
      edges: [...state.edges, newEdge],
      isDirty: true,
    }))
  },

  addNode: (moduleId, position) => {
    const moduleDef = getModuleDefinition(moduleId)
    if (!moduleDef) return ""

    const nodeId = `n_${generateId()}`

    // Build default config from all inputs that have defaults
    const config: Record<string, unknown> = {}
    for (const input of moduleDef.inputs) {
      if (input.config?.default !== undefined) {
        config[input.name] = input.config.default
      }
    }

    // Seed default model for AI generation nodes
    if (moduleDef.modelConfig) {
      const defaultModel = useModelCatalogStore.getState().getDefaultForModule(moduleId)
      if (defaultModel) {
        config.model = defaultModel.id
        config.provider = defaultModel.provider
      }
    }

    const data: NodeData = {
      module: moduleId,
      config,
      label: moduleDef.title,
      status: "idle",
    }

    if (moduleId === "agent_node" && !config.model) {
      config.model = "gemini-2.5-pro"
      config.provider = "google"
    }

    if (moduleId === "compositor") {
      Object.assign(config, JSON.parse(JSON.stringify(DEFAULT_COMPOSITOR_CONFIG)))
    }

    const newNode: PipelineNode = {
      id: nodeId,
      type: moduleId === "agent_node" ? "agent_node" : "module",
      position,
      data,
    }

    set((state) => ({
      nodes: [...state.nodes, newNode],
      isDirty: true,
    }))

    return nodeId
  },

  removeNode: (nodeId) => {
    set((state) => ({
      nodes: state.nodes.filter((n) => n.id !== nodeId),
      edges: state.edges.filter(
        (e) => e.source !== nodeId && e.target !== nodeId
      ),
      selectedNodeId:
        state.selectedNodeId === nodeId ? null : state.selectedNodeId,
      isDirty: true,
    }))
  },

  removeEdge: (edgeId) => {
    set((state) => ({
      edges: state.edges.filter((e) => e.id !== edgeId),
      isDirty: true,
    }))
  },

  duplicateNode: (nodeId, offset = { x: 50, y: 50 }) => {
    const { nodes } = get()
    const original = nodes.find((n) => n.id === nodeId)
    if (!original) return ""

    const newId = `n_${generateId()}`
    const newNode: PipelineNode = {
      id: newId,
      type: original.type,
      position: {
        x: original.position.x + offset.x,
        y: original.position.y + offset.y,
      },
      data: { ...original.data, config: { ...original.data.config } },
    }

    set((state) => ({
      nodes: [...state.nodes, newNode],
      isDirty: true,
    }))

    return newId
  },

  updateNodeConfig: (nodeId, configUpdate) => {
    set((state) => ({
      nodes: state.nodes.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                config: { ...node.data.config, ...configUpdate },
              },
            }
          : node
      ),
      isDirty: true,
    }))
  },

  updateNodeLabel: (nodeId, label) => {
    set((state) => ({
      nodes: state.nodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, label } }
          : node
      ),
      isDirty: true,
    }))
  },

  selectNode: (nodeId) => {
    set({ selectedNodeId: nodeId })
  },

  toggleNodeCollapsed: (nodeId) => {
    set((state) => {
      const next = new Set(state.collapsedNodeIds)
      if (next.has(nodeId)) {
        next.delete(nodeId)
      } else {
        next.add(nodeId)
      }
      return { collapsedNodeIds: next }
    })
  },

  addNodeRaw: (node) => {
    set((state) => ({ nodes: [...state.nodes, node] }))
  },

  removeNodeRaw: (nodeId) => {
    set((state) => ({
      nodes: state.nodes.filter((n) => n.id !== nodeId),
      edges: state.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
      selectedNodeId: state.selectedNodeId === nodeId ? null : state.selectedNodeId,
    }))
  },

  updateNodeRaw: (updatedNode) => {
    set((state) => ({
      nodes: state.nodes.map((n) => n.id === updatedNode.id ? updatedNode : n),
    }))
  },

  addEdgeRaw: (edge) => {
    set((state) => ({ edges: [...state.edges, edge] }))
  },

  removeEdgeRaw: (edgeId) => {
    set((state) => ({ edges: state.edges.filter((e) => e.id !== edgeId) }))
  },

  markClean: () => {
    set({ isDirty: false })
  },

  clearCanvas: () => {
    set({ nodes: [], edges: [], selectedNodeId: null, isDirty: true })
  },

  loadPipeline: (nodes, edges) => {
    set({ nodes, edges, selectedNodeId: null, isDirty: false, needsFitView: true })
  },
  needsFitView: false,
  clearFitView: () => set({ needsFitView: false }),

  isValidConnection: (connection) => {
    const { nodes, edges } = get()

    const source = connection.source
    const target = connection.target
    const sourceHandle = "sourceHandle" in connection ? connection.sourceHandle : undefined
    const targetHandle = "targetHandle" in connection ? connection.targetHandle : undefined

    // No self-connections
    if (source === target) return false

    // Must have all handles specified
    if (!source || !target || !sourceHandle || !targetHandle) return false

    // Target handle must not already have a connection (single input)
    const targetAlreadyConnected = edges.some(
      (e) => e.target === target && e.targetHandle === targetHandle
    )
    if (targetAlreadyConnected) return false

    // Type check: source output type must match target input type
    const sourceNode = nodes.find((n) => n.id === source)
    const targetNode = nodes.find((n) => n.id === target)
    if (!sourceNode || !targetNode) return false

    const sourceDef = getModuleDefinition(sourceNode.data.module)
    const targetDef = getModuleDefinition(targetNode.data.module)
    if (!sourceDef || !targetDef) return false

    // For llm_text with structured output schema, handles are dynamic
    let sourceOutput: { type: string } | undefined
    if (
      sourceNode.data.module === "llm_text" &&
      Array.isArray(sourceNode.data.config.output_schema) &&
      (sourceNode.data.config.output_schema as OutputSchemaField[]).length > 0
    ) {
      const schema = sourceNode.data.config.output_schema as OutputSchemaField[]
      sourceOutput = getStructuredOutputHandles(schema).find((o) => o.name === sourceHandle)
    }
    if (!sourceOutput) {
      sourceOutput = sourceDef.outputs.find((o) => o.name === sourceHandle)
    }

    let targetInput: { type: string } | undefined
    if (targetDef.module === "text_builder") {
      targetInput = getTextBuilderInputs(String(targetNode.data.config.template ?? "")).find(
        (i) => i.name === targetHandle
      )
    } else if (targetDef.module === "compositor") {
      targetInput = getCompositorInputs(targetNode.data.config).find(
        (i) => i.name === targetHandle
      )
    } else if (targetDef.modelConfig) {
      const modelEntry = useModelCatalogStore
        .getState()
        .getById(String(targetNode.data.config.model ?? ""))
      const param = modelEntry?.parameters_schema?.params?.find((p) => p.name === targetHandle)
      targetInput = param ? { type: PARAM_TYPE_TO_DATA_TYPE[param.type] } : undefined
    } else {
      targetInput = targetDef.inputs.find((i) => i.name === targetHandle && i.handle)
    }

    if (!sourceOutput || !targetInput) return false

    // trigger handles bypass type checking — they pass through any data type
    if (sourceOutput.type === "trigger" || targetInput.type === "trigger") return true

    if (targetInput.type === "media") {
      return ["image", "video", "string"].includes(sourceOutput.type)
    }

    return sourceOutput.type === targetInput.type
  },

  getRejectionReason: (connection) => {
    const { nodes, edges } = get()

    const source = connection.source
    const target = connection.target
    const sourceHandle = "sourceHandle" in connection ? connection.sourceHandle : undefined
    const targetHandle = "targetHandle" in connection ? connection.targetHandle : undefined

    if (!source || !target || !sourceHandle || !targetHandle) return null
    if (source === target) return "A node cannot connect to itself."

    const targetAlreadyConnected = edges.some(
      (e) => e.target === target && e.targetHandle === targetHandle
    )
    if (targetAlreadyConnected) return "This input already has a connection. Disconnect it first."

    const sourceNode = nodes.find((n) => n.id === source)
    const targetNode = nodes.find((n) => n.id === target)
    if (!sourceNode || !targetNode) return null

    const sourceDef = getModuleDefinition(sourceNode.data.module)
    const targetDef = getModuleDefinition(targetNode.data.module)
    if (!sourceDef || !targetDef) return null

    let sourceOutput: { type: string } | undefined
    if (
      sourceNode.data.module === "llm_text" &&
      Array.isArray(sourceNode.data.config.output_schema) &&
      (sourceNode.data.config.output_schema as OutputSchemaField[]).length > 0
    ) {
      const schema = sourceNode.data.config.output_schema as OutputSchemaField[]
      sourceOutput = getStructuredOutputHandles(schema).find((o) => o.name === sourceHandle)
    }
    if (!sourceOutput) {
      sourceOutput = sourceDef.outputs.find((o) => o.name === sourceHandle)
    }

    let targetInput: { type: string } | undefined
    if (targetDef.module === "text_builder") {
      targetInput = getTextBuilderInputs(String(targetNode.data.config.template ?? "")).find(
        (i) => i.name === targetHandle
      )
    } else if (targetDef.module === "compositor") {
      targetInput = getCompositorInputs(targetNode.data.config).find(
        (i) => i.name === targetHandle
      )
    } else if (targetDef.modelConfig) {
      const modelEntry = useModelCatalogStore
        .getState()
        .getById(String(targetNode.data.config.model ?? ""))
      const param = modelEntry?.parameters_schema?.params?.find((p) => p.name === targetHandle)
      targetInput = param ? { type: PARAM_TYPE_TO_DATA_TYPE[param.type] } : undefined
    } else {
      targetInput = targetDef.inputs.find((i) => i.name === targetHandle && i.handle)
    }

    if (!sourceOutput || !targetInput) return null

    if (sourceOutput.type !== targetInput.type &&
        sourceOutput.type !== "trigger" &&
        targetInput.type !== "trigger") {
      if (targetInput.type === "media" && ["image", "video", "string"].includes(sourceOutput.type)) {
        return null
      }
      return `Type mismatch: cannot connect ${sourceOutput.type} output to ${targetInput.type} input.`
    }

    return null
  },
}))
